<template>
    <div class="mb-2 md:mb-5" v-if="currentStep === 2 && selectedCard">
        <header class="flex justify-between items-center pb-2">
            <h2 class="text-slate-600 dark:text-slate-300 text-sm font-medium font-['Inter'] leading-tight">
                Details to copy
            </h2>
            <label class="flex items-center gap-2">
                <input
                    type="checkbox"
                    class="form-checkbox"
                    :checked="selectAll"
                    @change="$emit('toggle-select-all', $event.target.checked)"
                />
                <span class="whitespace-nowrap text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight">
                    Select all
                </span>
            </label>
        </header>
        <Errors :errors="errors['madeSelections']" class="pt-2" />
        <ul v-if="selectedCard" class="details-list transition-all">
            <li
                v-for="detail in copyableDetails"
                :key="`source-${detail.key}`"
                class="border-slate-200 dark:border-slate-700 border-b"
            >
                <div class="h-14 py-2">
                    <label class="flex items-center gap-3 h-full">
                        <input
                            type="checkbox"
                            class="form-checkbox"
                            :checked="selectedDetails[detail.key]"
                            @change="$emit('toggle-detail', detail.key, $event.target.checked)"
                        />
                        <div class="flex-1 flex flex-col justify-center">
                            <span class="text-slate-500 dark:text-slate-400 text-sm font-normal font-['Inter'] leading-tight">
                                {{ detail.label }}
                            </span>
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                                v-if="detail.key === 'welcome_bonus'"
                            >
                                <p>{{ selectedCard?.welcome_bonus ? '' : 'None' }}</p>
                            </span>
                            <span
                                class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight"
                                v-if="detail.key !== 'welcome_bonus'"
                            >
                                <p v-if="detail.key !== 'bonus_offers'">
                                    {{ formatCardDetails(selectedCard[detail.key], detail.key) }}
                                </p>
                                <p v-else>{{ selectedCard.bonus_offers?.length ? '' : 'None' }}</p>
                            </span>
                        </div>
                    </label>
                </div>
                <!-- Welcome bonus sub-details -->
                <Transition name="expand">
                    <template v-if="detail.key === 'welcome_bonus' && selectedDetails[detail.key]">
                        <ul
                            v-if="selectedCard?.welcome_bonus && selectedCard?.welcome_bonus?.type !== 'tiered'"
                            class="pb-4 -mt-1 -ml-1.5 pl-3"
                        >
                            <li
                                v-for="(subDetail, i) in welcomeBonusDetails"
                                :key="subDetail.key"
                                class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px]"
                                :class="i < welcomeBonusDetails.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                            >
                                <SubDetailConnector :index="i" :total="welcomeBonusDetails.length" />
                                <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ subDetail.label }}</span>
                                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                                    {{ formatCardDetails(selectedCard.welcome_bonus[subDetail.key], subDetail.key) }}
                                </span>
                            </li>
                        </ul>
                        <ul
                            v-else-if="selectedCard?.welcome_bonus?.type === 'tiered'"
                            class="pb-4 -mt-1 -ml-1.5 pl-3"
                        >
                            <li
                                v-for="(bonusTier, i) in selectedCard.welcome_bonus?.tiers"
                                :key="bonusTier.id"
                                class="relative flex justify-between pt-2 border-l pl-4 h-[52px]"
                                :class="i < selectedCard.welcome_bonus.tiers.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                            >
                                <SubDetailConnector :index="i" :total="selectedCard.welcome_bonus.tiers.length" />
                                <div class="flex flex-col gap-[2px]">
                                    <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ `Tier ${i + 1}` }}</span>
                                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">{{ formatBonusOfferDisplay(bonusTier) }}</span>
                                </div>
                                <BonusReceivedBadge :item="bonusTier" />
                            </li>
                        </ul>
                    </template>
                </Transition>
                <!-- Bonus offers sub-details -->
                <Transition name="expand">
                    <ul
                        v-if="detail.key === 'bonus_offers' && selectedDetails[detail.key] && selectedCard?.bonus_offers?.length"
                        class="pb-4 -mt-1 -ml-1.5 pl-3"
                    >
                        <li
                            v-for="(bonusOffer, i) in selectedCard.bonus_offers"
                            :key="bonusOffer.id"
                            class="relative flex justify-between pt-2 border-l pl-4 h-[52px]"
                            :class="i < selectedCard.bonus_offers.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                        >
                            <SubDetailConnector :index="i" :total="selectedCard.bonus_offers.length" />
                            <div class="flex flex-col gap-[2px]">
                                <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ $filters.titleCase(bonusOffer.type) }} offer</span>
                                <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">{{ formatBonusOfferDisplay(bonusOffer) }}</span>
                            </div>
                            <BonusReceivedBadge :item="bonusOffer" />
                        </li>
                    </ul>
                </Transition>
                <!-- Product changed sub-details -->
                <Transition name="expand">
                    <ul
                        v-if="detail.key === 'product_changed' && selectedDetails[detail.key] && selectedCard?.product_changed"
                        class="pb-4 -mt-1 -ml-1.5 pl-3"
                    >
                        <li
                            v-for="(subDetail, i) in productChangedDetails"
                            :key="subDetail.key"
                            class="relative flex flex-col gap-1 pt-2 border-l pl-4 h-[52px]"
                            :class="i < productChangedDetails.length - 1 ? 'border-slate-300 dark:border-slate-600' : 'border-transparent'"
                        >
                            <SubDetailConnector :index="i" :total="productChangedDetails.length" />
                            <span class="text-slate-600 dark:text-slate-300 text-sm font-normal">{{ subDetail.label }}</span>
                            <span class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight">
                                <span class="text-slate-800 dark:text-slate-100 text-sm font-normal font-['Inter'] leading-tight whitespace-nowrap truncate block"
                                    :title="subDetail.key === 'original_card' ? selectedCard?.original_card?.title : formatCardDetails(selectedCard[subDetail.key], subDetail.key)"
                                >
                                    {{ subDetail.key === 'original_card' ? selectedCard?.original_card?.title : formatCardDetails(selectedCard[subDetail.key], subDetail.key) }}
                                </span>
                            </span>
                        </li>
                    </ul>
                </Transition>
            </li>
        </ul>
    </div>
    <div
        v-else-if="!selectedCard"
        class="pt-6 pb-16 flex justify-center items-center flex-col"
    >
        <Icon icon="credit-cards" />
        <div class="pt-2 flex-col justify-start items-center gap-1 flex text-slate-500 dark:text-slate-400 text-sm font-normal font-['Inter'] leading-tight">
            Select a card to begin.
        </div>
    </div>
</template>

<script setup>
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import SubDetailConnector from './SubDetailConnector.vue';
import BonusReceivedBadge from './BonusReceivedBadge.vue';

defineProps({
    currentStep: { type: Number, required: true },
    selectedCard: { type: Object, default: null },
    selectedDetails: { type: Object, required: true },
    selectAll: { type: Boolean, default: false },
    errors: { type: Object, required: true },
    copyableDetails: { type: Array, required: true },
    welcomeBonusDetails: { type: Array, required: true },
    productChangedDetails: { type: Array, required: true },
    formatCardDetails: { type: Function, required: true },
    formatBonusOfferDisplay: { type: Function, required: true },
});

defineEmits(['toggle-detail', 'toggle-select-all']);
</script>
